import { useState, useRef, useCallback } from 'react';
import { api } from '@/lib/api';

export default function useGeocoding(token) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const timeoutRef = useRef(null);

  const geocode = useCallback((query) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    timeoutRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.post('/routing/geocode', { address: query });
        setSuggestions(res.data || []);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, [token]);

  const parseSuggestion = useCallback((suggestion) => {
    const display = suggestion.display_name || '';
    const parts = display.split(',').map(p => p.trim());
    // Parse Canadian + US addresses
    const provinces = {
      'Alberta': 'AB', 'British Columbia': 'BC', 'Manitoba': 'MB',
      'New Brunswick': 'NB', 'Newfoundland and Labrador': 'NL',
      'Nova Scotia': 'NS', 'Ontario': 'ON', 'Prince Edward Island': 'PE',
      'Quebec': 'QC', 'Saskatchewan': 'SK',
      'Northwest Territories': 'NT', 'Nunavut': 'NU', 'Yukon': 'YT',
      // US States
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR',
      'California': 'CA', 'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE',
      'Florida': 'FL', 'Georgia': 'GA', 'Hawaii': 'HI', 'Idaho': 'ID',
      'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA', 'Kansas': 'KS',
      'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
      'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV',
      'New Hampshire': 'NH', 'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY',
      'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK',
      'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT',
      'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV',
      'Wisconsin': 'WI', 'Wyoming': 'WY', 'District of Columbia': 'DC',
    };
    let province = '';
    for (const p of parts) {
      if (provinces[p]) { province = provinces[p]; break; }
    }
    // Match Canadian postal (A1A 1A1) or US ZIP (12345 or 12345-6789)
    const postal = parts.find(p => /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i.test(p) || /^\d{5}(-\d{4})?$/.test(p)) || '';
    return {
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon),
      street: parts[0] || '',
      city: parts[1] || '',
      province,
      postal,
      fullAddress: display,
    };
  }, []);

  const clearSuggestions = useCallback(() => setSuggestions([]), []);

  return { suggestions, loading, geocode, parseSuggestion, clearSuggestions };
}
